import { convert, type Converter } from './converters';
import { formatResult } from './format';

const HASH_PREFIX = '#v=';

export class Calculator {
  private el: HTMLElement;
  private converter: Converter;
  private debounceTimer?: number;
  private suppressHash = false;

  constructor(el: HTMLElement, converter: Converter) {
    this.el = el;
    this.converter = converter;
  }

  init() {
    const inputEl = this.el.querySelector('[data-calc-input]') as HTMLInputElement;
    inputEl?.addEventListener('input', () => this.debouncedRecompute());
    this.el.querySelectorAll('select').forEach(s => s.addEventListener('change', () => this.recompute()));
    this.el.querySelector('[data-calc-swap]')?.addEventListener('click', () => this.swap());
    this.el.querySelectorAll('[data-condition]').forEach(i => {
      i.addEventListener('input', () => {
        this.debouncedRecompute();
        this.validateCondition(i as HTMLInputElement);
      });
    });
    this.el.querySelector('[data-calc-copy]')?.addEventListener('click', () => this.copyResult());

    this.el.addEventListener('keydown', (e) => this.handleKeyboard(e as KeyboardEvent));

    document.querySelectorAll('[data-fill-example]').forEach(b => {
      b.addEventListener('click', (e) => {
        const json = (e.currentTarget as HTMLElement).dataset.fillExample!;
        this.fillExample(JSON.parse(json));
      });
    });

    this.el.querySelectorAll('[data-try-value]').forEach(b => {
      b.addEventListener('click', (e) => {
        const val = (e.currentTarget as HTMLElement).dataset.tryValue!;
        const inputEl = this.el.querySelector('[data-calc-input]') as HTMLInputElement;
        if (inputEl) {
          inputEl.value = val;
          this.recompute();
          inputEl.focus();
        }
      });
    });

    this.restoreFromHash();
    this.recompute();
    window.addEventListener('hashchange', () => {
      if (this.suppressHash) { this.suppressHash = false; return; }
      this.restoreFromHash();
      this.recompute();
    });
  }

  private handleKeyboard(e: KeyboardEvent) {
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'SELECT') {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.recompute();
      }
      if (e.key === 'Escape') {
        const details = this.el.querySelector('.advanced') as HTMLDetailsElement;
        if (details && details.open) {
          details.open = false;
          (this.el.querySelector('[data-calc-input]') as HTMLInputElement)?.focus();
        }
      }
    }
  }

  private validateCondition(input: HTMLInputElement) {
    const key = input.dataset.condition!;
    const specs = this.converter.conditions?.filter(c => c.key === key) ?? [];
    if (!specs.length) return;
    const spec = specs[0];
    const val = parseFloat(input.value);
    const errorEl = this.el.querySelector(`[data-condition-error-for="${key}"]`) as HTMLElement | null;
    if (isNaN(val)) {
      input.classList.remove('input-error');
      if (errorEl) { errorEl.classList.add('hidden'); errorEl.textContent = ''; }
      input.removeAttribute('title');
      return;
    }
    const outOfRange = (spec.min !== undefined && val < spec.min) || (spec.max !== undefined && val > spec.max);
    input.classList.toggle('input-error', outOfRange);
    if (outOfRange) {
      const lo = spec.min ?? '—';
      const hi = spec.max ?? '—';
      input.title = `Out of range (${lo} to ${hi})`;
      if (errorEl) {
        errorEl.textContent = `Value must be between ${lo} and ${hi}.`;
        errorEl.classList.remove('hidden');
      }
    } else {
      input.removeAttribute('title');
      if (errorEl) { errorEl.classList.add('hidden'); errorEl.textContent = ''; }
    }
  }

  private debouncedRecompute() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = window.setTimeout(() => this.recompute(), 200);
  }

  private readState() {
    const value = parseFloat((this.el.querySelector('[data-calc-input]') as HTMLInputElement).value);
    const fromUnit = (this.el.querySelector('[data-calc-from-unit]') as HTMLSelectElement).value;
    const toUnit = (this.el.querySelector('[data-calc-to-unit]') as HTMLSelectElement).value;
    const conds: Record<string, number> = {};
    this.el.querySelectorAll('[data-condition]').forEach((el) => {
      const k = (el as HTMLInputElement).dataset.condition!;
      const v = parseFloat((el as HTMLInputElement).value);
      if (!isNaN(v)) conds[k] = v;
    });
    return { value, fromUnit, toUnit, conds };
  }

  private recompute() {
    const inputEl = this.el.querySelector('[data-calc-input]') as HTMLInputElement;
    const value = parseFloat(inputEl.value);
    const fromUnit = (this.el.querySelector('[data-calc-from-unit]') as HTMLSelectElement).value;
    const toUnit = (this.el.querySelector('[data-calc-to-unit]') as HTMLSelectElement).value;
    const outputEl = this.el.querySelector('[data-calc-output]') as HTMLOutputElement;
    const narr = this.el.querySelector('[data-result-narrative]') as HTMLElement;
    if (isNaN(value)) {
      outputEl.textContent = '—';
      if (narr) narr.textContent = 'Enter a value above to see the result.';
      return;
    }
    const conds: Record<string, number> = {};
    this.el.querySelectorAll('[data-condition]').forEach((el) => {
      conds[(el as HTMLInputElement).dataset.condition!] = parseFloat((el as HTMLInputElement).value);
    });
    try {
      const result = convert(this.converter, value, fromUnit, toUnit, conds);
      const formatted = formatResult(result);
      outputEl.textContent = formatted;
      outputEl.dataset.pulse = 'true';
      window.setTimeout(() => { outputEl.dataset.pulse = 'false'; }, 320);
      if (narr) {
        const isPureCalc = this.converter.type === 'formula' && this.converter.formulaJs && !/\bv\b/.test(this.converter.formulaJs);
        if (isPureCalc) {
          const parts = Object.entries(conds).filter(([,v]) => !isNaN(v)).map(([k, v]) => `${k}=${v}`);
          narr.textContent = `Result: ${formatted} ${toUnit.toUpperCase()}${parts.length ? ' (using ' + parts.join(', ') + ')' : ''}`;
        } else {
          narr.textContent = `${value} ${fromUnit.toUpperCase()} = ${formatted} ${toUnit.toUpperCase()}`;
        }
      }
      this.syncHash(value, fromUnit, toUnit, conds);
    } catch (e) {
      outputEl.textContent = 'Error';
      if (narr) narr.textContent = (e as Error).message || 'Calculation failed.';
    }
  }

  private swap() {
    const fromSel = this.el.querySelector('[data-calc-from-unit]') as HTMLSelectElement;
    const toSel = this.el.querySelector('[data-calc-to-unit]') as HTMLSelectElement;
    [fromSel.value, toSel.value] = [toSel.value, fromSel.value];
    this.recompute();
  }

  private fillExample(ex: { input: number; conditions?: Record<string, number> }) {
    (this.el.querySelector('[data-calc-input]') as HTMLInputElement).value = String(ex.input);
    if (ex.conditions) {
      Object.entries(ex.conditions).forEach(([k, v]) => {
        const el = this.el.querySelector(`[data-condition="${k}"]`) as HTMLInputElement;
        if (el) {
          el.value = String(v);
          this.validateCondition(el);
        }
      });
    }
    this.recompute();
    this.el.scrollIntoView({ behavior: 'smooth' });
  }

  private copyResult() {
    const outputEl = this.el.querySelector('[data-calc-output]') as HTMLOutputElement;
    const labelEl = this.el.querySelector('[data-calc-copy-label]') as HTMLElement | null;
    const text = outputEl.textContent?.trim();
    if (!text || text === '—' || text === 'Error') return;
    const fromUnit = (this.el.querySelector('[data-calc-from-unit]') as HTMLSelectElement).value;
    const toUnit = (this.el.querySelector('[data-calc-to-unit]') as HTMLSelectElement).value;
    const value = (this.el.querySelector('[data-calc-input]') as HTMLInputElement).value;
    const fullText = `${value} ${fromUnit.toUpperCase()} = ${text} ${toUnit.toUpperCase()}`;
    const finish = (ok: boolean) => {
      if (!labelEl) return;
      const original = 'Copy';
      labelEl.textContent = ok ? 'Copied' : 'Failed';
      window.setTimeout(() => { labelEl.textContent = original; }, 1400);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(fullText).then(() => finish(true)).catch(() => finish(false));
    } else {
      const ta = document.createElement('textarea');
      ta.value = fullText;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); finish(true); } catch { finish(false); }
      document.body.removeChild(ta);
    }
  }

  private syncHash(value: number, fromUnit: string, toUnit: string, conds: Record<string, number>) {
    const parts: string[] = [`v=${value}`, `from=${fromUnit}`, `to=${toUnit}`];
    for (const [k, v] of Object.entries(conds)) {
      parts.push(`${k}=${v}`);
    }
    this.suppressHash = true;
    history.replaceState(null, '', HASH_PREFIX + parts.join('&'));
  }

  private restoreFromHash() {
    const hash = window.location.hash;
    if (!hash.startsWith(HASH_PREFIX)) return;
    const params = new URLSearchParams(hash.slice(HASH_PREFIX.length));
    const inputEl = this.el.querySelector('[data-calc-input]') as HTMLInputElement;
    const fromSel = this.el.querySelector('[data-calc-from-unit]') as HTMLSelectElement;
    const toSel = this.el.querySelector('[data-calc-to-unit]') as HTMLSelectElement;
    const v = params.get('v');
    if (v !== null) inputEl.value = v;
    const from = params.get('from');
    if (from && fromSel.tagName === 'SELECT' && Array.from(fromSel.options).some(o => o.value === from)) fromSel.value = from;
    const to = params.get('to');
    if (to && toSel.tagName === 'SELECT' && Array.from(toSel.options).some(o => o.value === to)) toSel.value = to;
    this.converter.conditions?.forEach(spec => {
      const raw = params.get(spec.key);
      if (raw === null) return;
      const el = this.el.querySelector(`[data-condition="${spec.key}"]`) as HTMLInputElement | null;
      if (!el) return;
      el.value = raw;
      this.validateCondition(el);
    });
  }
}
